import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import {
  categories,
  locations,
  listings,
  blogPosts,
  listingHref,
  blogHref,
  comboHref,
} from "@/lib/data";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/sitemap/")({
  head: () => ({
    meta: [
      { title: "Sitemap — All Pages | LetsListBusiness" },
      { name: "description", content: "HTML sitemap for LetsListBusiness. Browse every category, city, listing and article." },
    ],
    links: [{ rel: "canonical", href: SITE_URL + "/sitemap/" }],
  }),
  component: SitemapPage,
});

function SitemapPage() {
  const totalPages =
    11 + categories.length + locations.length + blogPosts.length +
    categories.length * locations.length + listings.length;

  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Sitemap" }]} />

      {/* Header */}
      <section className="container mx-auto border-b border-border px-4 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Index · /sitemap</div>
            <h1 className="mt-3 font-display text-5xl font-bold sm:text-6xl">Sitemap</h1>
          </div>
          <div className="font-mono text-sm text-muted-foreground">
            {totalPages.toLocaleString()} pages indexed
          </div>
        </div>
      </section>

      {/* Dense column layout */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid gap-x-10 gap-y-12 lg:grid-cols-3">
          <Column title="Main" count={11}>
            <Item to="/">Home</Item>
            <Item to="/category">All categories</Item>
            <Item to="/location">All cities</Item>
            <Item to="/blog">Blog</Item>
            <Item to="/about">About</Item>
            <Item to="/contact">Contact</Item>
            <Item to="/add-business">Add a business</Item>
            <Item to="/login">Log in</Item>
            <Item to="/signup">Sign up</Item>
            <Item to="/privacy-policy">Privacy policy</Item>
            <Item to="/terms">Terms</Item>
          </Column>

          <Column title="Categories" count={categories.length}>
            {categories.map((c) => (
              <li key={c.slug}>
                <Link to="/category/$slug" params={{ slug: c.slug }} className="block py-1 text-sm text-foreground hover:text-primary">
                  <span className="mr-2 text-muted-foreground">{c.icon}</span>{c.name}
                </Link>
              </li>
            ))}
          </Column>

          <Column title="Cities" count={locations.length}>
            {locations.map((l) => (
              <li key={l.slug}>
                <Link to="/location/$slug" params={{ slug: l.slug }} className="block py-1 text-sm text-foreground hover:text-primary">
                  {l.name}, <span className="text-muted-foreground">{l.state}</span>
                </Link>
              </li>
            ))}
          </Column>

          <Column title="Blog articles" count={blogPosts.length}>
            {blogPosts.map((p) => (
              <li key={p.slug}>
                <Link {...blogHref(p.slug)} className="block py-1 text-sm text-foreground hover:text-primary">{p.title}</Link>
              </li>
            ))}
          </Column>

          <Column title="Category × City" count={categories.length * locations.length} wide>
            {locations.flatMap((l) =>
              categories.map((c) => (
                <li key={`${l.slug}-${c.slug}`}>
                  <Link {...comboHref(l.slug, c.slug)} className="block py-1 text-sm text-foreground hover:text-primary">
                    {c.name} <span className="text-muted-foreground">in</span> {l.name}
                  </Link>
                </li>
              )),
            )}
          </Column>
        </div>

        {/* Listings — full-width dense grid */}
        <div className="mt-16 border-t border-border pt-10">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-2xl font-bold">All listings</h2>
            <span className="font-mono text-xs text-muted-foreground">{listings.length} total</span>
          </div>
          <ul className="mt-6 grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.map((l) => (
              <li key={l.id}>
                <Link {...listingHref(l)} className="block truncate py-1 text-sm text-foreground hover:text-primary">
                  <span className="font-mono text-xs text-muted-foreground">#{l.id}</span> {l.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}

function Column({ title, count, wide, children }: { title: string; count: number; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? "lg:col-span-3" : ""}>
      <div className="mb-3 flex items-baseline justify-between border-b border-border pb-2">
        <h2 className="font-display text-lg font-bold">{title}</h2>
        <span className="font-mono text-[10px] text-muted-foreground">{count.toString().padStart(3, "0")}</span>
      </div>
      <ul className={wide ? "grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3" : ""}>
        {children}
      </ul>
    </div>
  );
}

function Item({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="block py-1 text-sm text-foreground hover:text-primary">{children}</Link>
    </li>
  );
}
